/* Sui Story — 3D embroidered headline (the brand signature, in thread).
   ----------------------------------------------------------------------
   Renders a headline as real satin-stitch embroidery using Three.js:
   the text is rasterised in the brand serif, a distance transform derives
   the across-stroke direction of every stitch, and a few thousand raised
   "thread" capsules (logo brown #5A3A2D) are laid down to fill each letter.
   As the section scrolls into view the stitches are drawn in, left-to-right,
   so the headline embroiders itself in real time.

   Progressive enhancement:
     • No WebGL / Three fails to load  -> the plain styled <h2> stays visible.
     • prefers-reduced-motion          -> stitches render fully formed (no scrub).
     • Three loads only on pages that actually contain [data-embroidery3d].

   Mounts via the shared lifecycle: re-inits on `suistory:page:init`
   (fired by theme.js on first load and after every Barba swap). Idempotent.
*/
(function () {
  'use strict';
  var S = (window.SuiStory = window.SuiStory || {});

  /* ---- Tunable look & feel (safe to nudge) ---------------------------- */
  var CONFIG = {
    color: '#5A3A2D',            // logo brown — the thread colour
    // A thick, even-weight script (Palermo-style) so satin has solid strokes to fill.
    fontFamily: "'Pacifico', 'Dancing Script', cursive",
    fontWeight: 400,
    letterSpacingEm: 0,
    minFitPx: 16,                // floor for the auto-fit single-line size (css px)
    maxFontPx: 60,               // cap on the single-line size (css px)
    lineHeight: 1.32,            // script needs room for ascenders/descenders/swashes
    padX: 16,                    // horizontal breathing room inside the canvas (css px)
    padY: 22,                    // vertical breathing room (css px)
    maskScale: 2,                // rasterise mask at Nx css px for finer stitch placement
    /* Satin fill: cross-stroke stitches snapped to the stroke centreline and
       packed tightly along it, so the thread reads solid and directional. */
    threadWidth: 3.1,            // thickness of each satin stitch, along the stroke (css px)
    advance: 1.95,               // spacing of stitches along the stroke centreline (css px)
    minStitchLen: 3,             // shortest stitch span (css px)
    maxStitchLen: 26,            // caps the span where strokes cross/thicken (css px)
    ascentSteps: 7,              // gradient-ascent steps used to reach the centreline
    overshoot: 1.08,             // satin slightly overshoots the stroke edges
    posJitter: 0.5,              // css px
    angleJitter: 0.07,           // radians — keep small so rows stay parallel
    lenJitter: 0.06,             // fraction of length
    shadeJitter: 0.13,           // brightness variation between threads
    raise: 0.9,                  // how far stitches sit above the cloth (× threadWidth)
    revealWindow: 0.06,          // how "soft" the stitch-in wipe is, in progress units
    diagonalBias: 0.0,           // 0 = pure left-to-right "writing" reveal order (script)
    maxStitches: 12000           // hard safety cap (advance auto-relaxes if exceeded)
  };

  /* ---- small helpers -------------------------------------------------- */
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function easeOutCubic(t) { t = 1 - t; return 1 - t * t * t; }
  function mulberry(seed) { // deterministic PRNG so re-renders are stable
    return function () {
      seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
      var t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function prefersReduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function webglSupported() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) { return false; }
  }
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.replace(/(.)/g, '$1$1');
    return {
      r: parseInt(hex.slice(0, 2), 16) / 255,
      g: parseInt(hex.slice(2, 4), 16) / 255,
      b: parseInt(hex.slice(4, 6), 16) / 255
    };
  }

  /* Lazily inject three.min.js (vendored asset) — once per page. */
  var threePromise = null;
  function loadThree(src) {
    if (window.THREE) return Promise.resolve();
    if (threePromise) return threePromise;
    threePromise = new Promise(function (resolve, reject) {
      if (!src) { reject(new Error('no three src')); return; }
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = function () { window.THREE ? resolve() : reject(new Error('THREE missing')); };
      s.onerror = function () { reject(new Error('three load error')); };
      document.head.appendChild(s);
    });
    return threePromise;
  }

  /* ===================================================================== */
  /*  Embroidery3D — one controller per [data-embroidery3d] element        */
  /* ===================================================================== */
  function Embroidery3D(el) {
    this.el = el;
    this.text = (el.getAttribute('data-text') || el.textContent || '').replace(/\s*\|\s*/g, ' ').trim();
    this.color = el.getAttribute('data-color') || CONFIG.color;
    this.canvas = el.querySelector('.embroidery3d__canvas');
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'embroidery3d__canvas';
      this.canvas.setAttribute('aria-hidden', 'true');
      el.appendChild(this.canvas);
    }
    this.cur = 0;        // displayed progress
    this.target = 0;     // scroll-driven target progress
    this.raf = 0;
    this.st = null;      // ScrollTrigger
    this.built = false;
    this.reduced = prefersReduced();

    this._initRenderer();
    this._bindResize();
    this._bindScroll();
    var self = this;
    this._whenFontReady(function () { self._build(); });
  }

  /* Ensure the script webfont is actually loaded before we rasterise the
     glyphs (otherwise the canvas measures/draws a fallback face). */
  Embroidery3D.prototype._whenFontReady = function (cb) {
    var probe = CONFIG.fontWeight + ' 48px ' + CONFIG.fontFamily;
    if (document.fonts && document.fonts.load) {
      try {
        document.fonts.load(probe, this.text).then(function () {
          (document.fonts.ready ? document.fonts.ready.then(cb, cb) : cb());
        }, cb);
        return;
      } catch (e) {}
    }
    setTimeout(cb, 80);
  };

  Embroidery3D.prototype._initRenderer = function () {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas, alpha: true, antialias: true, premultipliedAlpha: true
    });
    this.renderer.setClearColor(0x000000, 0);
    if ('outputEncoding' in this.renderer && THREE.sRGBEncoding) {
      this.renderer.outputEncoding = THREE.sRGBEncoding;
    }
    this.scene = new THREE.Scene();

    // Warm key light from top-left-front -> highlight on the upper side of each
    // thread; soft ambient keeps the brown readable; a dim cool fill adds depth.
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.66));
    var key = new THREE.DirectionalLight(0xfff3e2, 1.0);
    key.position.set(-0.5, 0.85, 0.95);
    this.scene.add(key);
    var fill = new THREE.DirectionalLight(0xd8e2ff, 0.25);
    fill.position.set(0.7, -0.35, 0.5);
    this.scene.add(fill);

    this.material = new THREE.MeshPhongMaterial({
      color: 0xffffff,                       // multiplied by per-instance colour
      specular: new THREE.Color(0x8a6a52),   // warm thread sheen
      shininess: 32
    });

    // Unit capsule: radius 0.5, cylinder length 1 -> overall Y extent = 2, Ø = 1.
    var hasCapsule = typeof THREE.CapsuleGeometry === 'function';
    this.stitchGeo = hasCapsule
      ? new THREE.CapsuleGeometry(0.5, 1, 3, 6)
      : new THREE.CylinderGeometry(0.5, 0.5, 2, 8);

    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, 3000);
    this.camera.position.z = 1000;
  };

  /* Rasterise the text, return { inside, w, h, cssW, cssH } in mask pixels. */
  Embroidery3D.prototype._rasterise = function (cssW) {
    var MS = CONFIG.maskScale;
    var mc = document.createElement('canvas');
    var ctx = mc.getContext('2d');

    // Auto-size so the WHOLE headline fits on one line at any width (canvas text
    // width scales linearly with font-size), capped at maxFontPx on desktop.
    var avail = Math.max(40, cssW - CONFIG.padX * 2);
    ctx.font = CONFIG.fontWeight + ' 100px ' + CONFIG.fontFamily;
    var w100 = ctx.measureText(this.text).width || 1;
    var fontPx = clamp(avail * 100 / w100, CONFIG.minFitPx, CONFIG.maxFontPx);
    var fontStr = CONFIG.fontWeight + ' ' + fontPx + 'px ' + CONFIG.fontFamily;

    // Hug the glyph ink (no fixed line-box padding) so the headline box adds no
    // hidden vertical gap. Safety covers anti-aliasing + thread width/overshoot.
    ctx.font = fontStr;
    var tm = ctx.measureText(this.text);
    var ascent = tm.actualBoundingBoxAscent || fontPx * 0.86;
    var descent = tm.actualBoundingBoxDescent || fontPx * 0.34;
    var safety = Math.max(5, Math.round(CONFIG.threadWidth + fontPx * 0.05));
    var cssH = Math.ceil(ascent + descent + safety * 2);
    var baseline = safety + ascent;

    mc.width = Math.ceil(cssW * MS);
    mc.height = Math.ceil(cssH * MS);
    ctx.setTransform(MS, 0, 0, MS, 0, 0);    // draw in css px, sample at MS resolution
    ctx.font = fontStr;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#fff';
    ctx.fillText(this.text, cssW / 2, baseline);

    var w = mc.width, h = mc.height;
    var data = ctx.getImageData(0, 0, w, h).data;
    var inside = new Uint8Array(w * h);
    for (var p = 0; p < w * h; p++) inside[p] = data[p * 4 + 3] > 96 ? 1 : 0;
    return { inside: inside, w: w, h: h, cssW: cssW, cssH: cssH };
  };

  /* Two-pass chamfer distance transform: distance (mask px) from each
     interior pixel to the nearest edge. Drives stitch length + direction. */
  function distanceField(inside, w, h) {
    var INF = 1e9, d = new Float32Array(w * h), R2 = Math.SQRT2, i, x, y;
    for (i = 0; i < d.length; i++) d[i] = inside[i] ? INF : 0;
    function rd(x, y) { return (x < 0 || y < 0 || x >= w || y >= h) ? 0 : d[y * w + x]; }
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
      if (!inside[y * w + x]) continue;
      var m = d[y * w + x];
      m = Math.min(m, rd(x - 1, y) + 1, rd(x, y - 1) + 1, rd(x - 1, y - 1) + R2, rd(x + 1, y - 1) + R2);
      d[y * w + x] = m;
    }
    for (y = h - 1; y >= 0; y--) for (x = w - 1; x >= 0; x--) {
      if (!inside[y * w + x]) continue;
      var n = d[y * w + x];
      n = Math.min(n, rd(x + 1, y) + 1, rd(x, y + 1) + 1, rd(x + 1, y + 1) + R2, rd(x - 1, y + 1) + R2);
      d[y * w + x] = n;
    }
    return d;
  }

  /* Light 3x3 box blur — smooths the distance field so gradient ascent and
     stitch direction stay coherent instead of noisy. */
  function blur3(src, w, h) {
    var out = new Float32Array(w * h);
    for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) {
      var s = 0, n = 0;
      for (var dy = -1; dy <= 1; dy++) for (var dx = -1; dx <= 1; dx++) {
        var xx = x + dx, yy = y + dy;
        if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
        s += src[yy * w + xx]; n++;
      }
      out[y * w + x] = s / n;
    }
    return out;
  }

  /* Build the per-stitch list from the rasterised glyph mask. */
  Embroidery3D.prototype._computeStitches = function (mask) {
    var w = mask.w, h = mask.h, inside = mask.inside, MS = CONFIG.maskScale;
    var dist = blur3(distanceField(inside, w, h), w, h);

    function D(x, y) { x = x | 0; y = y | 0; return (x < 0 || y < 0 || x >= w || y >= h) ? 0 : dist[y * w + x]; }
    function gradX(x, y) { return D(x + 1, y) - D(x - 1, y); }
    function gradY(x, y) { return D(x, y + 1) - D(x, y - 1); }

    // Auto-relax stitch spacing if a very large headline would exceed the cap.
    var area = 0, k;
    for (k = 0; k < inside.length; k++) if (inside[k]) area++;
    var advanceM = Math.max(1.4, CONFIG.advance * MS);
    while (area / (advanceM * advanceM) > CONFIG.maxStitches * 2) advanceM *= 1.2;

    var minLenM = CONFIG.minStitchLen * MS, maxLenM = CONFIG.maxStitchLen * MS, tw = CONFIG.threadWidth;
    var sampleStep = Math.max(1, Math.round(advanceM * 0.6));
    var rand = mulberry(0x5A3A2D ^ (w * 131 + h));

    // Spatial hash to keep one stitch per ~advance along each centreline.
    var cell = advanceM, minD2 = (advanceM * 0.86) * (advanceM * 0.86), occ = {};
    function tooClose(x, y) {
      var cx = Math.floor(x / cell), cy = Math.floor(y / cell);
      for (var oy = cy - 1; oy <= cy + 1; oy++) for (var ox = cx - 1; ox <= cx + 1; ox++) {
        var arr = occ[ox + ',' + oy]; if (!arr) continue;
        for (var i = 0; i < arr.length; i += 2) {
          var dx = arr[i] - x, dy = arr[i + 1] - y;
          if (dx * dx + dy * dy < minD2) return true;
        }
      }
      return false;
    }
    function mark(x, y) { var key = Math.floor(x / cell) + ',' + Math.floor(y / cell); (occ[key] || (occ[key] = [])).push(x, y); }

    var stitches = [];
    for (var gy = 1; gy < h - 1; gy += sampleStep) {
      for (var gx = 1; gx < w - 1; gx += sampleStep) {
        if (!inside[gy * w + gx]) continue;
        // Direction ACROSS the stroke comes from the gradient at this (off-centre)
        // sample point — well-defined here, and constant along the ascent path.
        var g0x = gradX(gx, gy), g0y = gradY(gx, gy);
        var g0 = Math.sqrt(g0x * g0x + g0y * g0y);

        // Climb the gradient to the stroke centreline (distance ridge).
        var px = gx, py = gy;
        for (var s = 0; s < CONFIG.ascentSteps; s++) {
          var gxv = gradX(px, py), gyv = gradY(px, py);
          var gl = Math.sqrt(gxv * gxv + gyv * gyv);
          if (gl < 1e-3) break;
          var nx = px + gxv / gl, ny = py + gyv / gl;
          if (nx < 1 || ny < 1 || nx >= w - 1 || ny >= h - 1) break;
          if (!inside[(ny | 0) * w + (nx | 0)]) break;
          if (D(nx, ny) <= D(px, py) + 1e-3) break;   // reached the ridge
          px = nx; py = ny;
        }
        if (tooClose(px, py)) continue;
        var dC = D(px, py);
        if (dC < 0.6) continue;
        mark(px, py);

        var ang;
        if (g0 > 1e-3) ang = Math.atan2(g0y, g0x);    // across-stroke axis
        else ang = Math.atan2(gradY(px + 1, py) - gradY(px - 1, py), gradX(px + 1, py) - gradX(px - 1, py));
        ang += (rand() - 0.5) * 2 * CONFIG.angleJitter;

        // Span the full local stroke width (edge to edge), with a touch of overshoot.
        var len = clamp(2 * dC * CONFIG.overshoot, minLenM, maxLenM) * (1 + (rand() - 0.5) * 2 * CONFIG.lenJitter);

        stitches.push({
          x: (px + (rand() - 0.5) * 2 * CONFIG.posJitter * MS) / MS,
          y: (py + (rand() - 0.5) * 2 * CONFIG.posJitter * MS) / MS,
          len: len / MS,
          ang: ang,
          shade: 1 + (rand() - 0.5) * 2 * CONFIG.shadeJitter,
          z: tw * CONFIG.raise * (0.55 + rand() * 0.7),
          order: (px + py * CONFIG.diagonalBias) / (w + h * CONFIG.diagonalBias)
        });
      }
    }

    stitches.sort(function (a, b) { return a.order - b.order; });
    if (stitches.length) {  // normalise draw order to span exactly [0,1]
      var lo = stitches[0].order, hi = stitches[stitches.length - 1].order, sp = (hi - lo) || 1;
      for (var i2 = 0; i2 < stitches.length; i2++) stitches[i2].order = (stitches[i2].order - lo) / sp;
    }
    return stitches;
  };

  Embroidery3D.prototype._build = function () {
    var el = this.el;
    var cssW = Math.round(el.clientWidth);
    if (cssW < 40) { // not laid out yet — retry shortly
      var self = this;
      this._retries = (this._retries || 0) + 1;
      if (this._retries < 20) { requestAnimationFrame(function () { self._build(); }); }
      else { el.classList.add('e3d--fallback'); }
      return;
    }

    var mask = this._rasterise(cssW);
    var stitches = this._computeStitches(mask);
    if (!stitches.length) { el.classList.add('e3d--fallback'); return; }

    var cssH = mask.cssH;
    // Reserve layout height + reveal the canvas (hides the plain heading).
    el.style.height = cssH + 'px';
    el.classList.add('is-active');

    // Renderer / camera in css-pixel world units (y-down -> world y = -y).
    var PR = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.setPixelRatio(PR);
    this.renderer.setSize(cssW, cssH, false);
    this.camera.left = 0; this.camera.right = cssW;
    this.camera.top = 0; this.camera.bottom = -cssH;
    this.camera.updateProjectionMatrix();

    // (Re)build the instanced thread mesh.
    if (this.mesh) { this.scene.remove(this.mesh); this.mesh.dispose && this.mesh.dispose(); this.mesh = null; }
    var count = stitches.length;
    var mesh = new THREE.InstancedMesh(this.stitchGeo, this.material, count);
    mesh.frustumCulled = false;

    var base = hexToRgb(this.color);
    var col = new THREE.Color();
    var W = CONFIG.threadWidth;
    var dummyPos = new THREE.Vector3(), dummyQuat = new THREE.Quaternion(),
        dummyScale = new THREE.Vector3(), m4 = new THREE.Matrix4(), zAxis = new THREE.Vector3(0, 0, 1);

    this.stitches = stitches;
    this.basePos = new Float32Array(count * 3);
    this.baseRotZ = new Float32Array(count);
    this.baseLen = new Float32Array(count);
    this.order = new Float32Array(count);
    this.appearPrev = new Float32Array(count); // all 0

    for (var i = 0; i < count; i++) {
      var s = stitches[i];
      this.basePos[i * 3] = s.x; this.basePos[i * 3 + 1] = -s.y; this.basePos[i * 3 + 2] = s.z;
      // capsule axis is +Y (at 90deg); rotate it to the stitch's world axis.
      // world axis angle = -ang (world y = -css y); offset by -90deg for the +Y geometry.
      this.baseRotZ[i] = -s.ang - Math.PI / 2;
      this.baseLen[i] = s.len;
      this.order[i] = s.order;

      // per-thread colour (clamped brown shade)
      col.setRGB(clamp(base.r * s.shade, 0, 1), clamp(base.g * s.shade, 0, 1), clamp(base.b * s.shade, 0, 1));
      mesh.setColorAt(i, col);

      // start hidden (scale 0)
      dummyPos.set(this.basePos[i * 3], this.basePos[i * 3 + 1], this.basePos[i * 3 + 2]);
      dummyQuat.setFromAxisAngle(zAxis, this.baseRotZ[i]);
      dummyScale.set(0, 0, 0);
      m4.compose(dummyPos, dummyQuat, dummyScale);
      mesh.setMatrixAt(i, m4);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    this.scene.add(mesh);
    this.mesh = mesh;
    this._tmp = { pos: dummyPos, quat: dummyQuat, scale: dummyScale, m4: m4, zAxis: zAxis, W: W };
    this.built = true;

    if (this.reduced) { this.cur = this.target = 1; this._applyProgress(1); this._render(); }
    else { this._applyProgress(this.cur); this._render(); }

    if (window.ScrollTrigger) { try { ScrollTrigger.refresh(); } catch (e) {} }
  };

  /* Recompose instance matrices for the given progress (0..1). */
  Embroidery3D.prototype._applyProgress = function (p) {
    if (!this.mesh) return;
    var t = this._tmp, count = this.stitches.length, W = t.W;
    var win = CONFIG.revealWindow, pp = p * (1 + win);
    var changed = false;
    for (var i = 0; i < count; i++) {
      var raw = (pp - this.order[i]) / win;
      var a = raw <= 0 ? 0 : raw >= 1 ? 1 : easeOutCubic(raw);
      if (Math.abs(a - this.appearPrev[i]) < 0.002 && a !== 1 && a !== 0) continue;
      if (a === this.appearPrev[i]) continue;
      this.appearPrev[i] = a;
      changed = true;
      t.pos.set(this.basePos[i * 3], this.basePos[i * 3 + 1], this.basePos[i * 3 + 2]);
      t.quat.setFromAxisAngle(t.zAxis, this.baseRotZ[i]);
      // scaleY drives overall length (capsule Y extent = 2*scaleY); X/Z = thread width.
      t.scale.set(W * a, (this.baseLen[i] / 2) * a, W * a);
      t.m4.compose(t.pos, t.quat, t.scale);
      this.mesh.setMatrixAt(i, t.m4);
    }
    if (changed) this.mesh.instanceMatrix.needsUpdate = true;
    return changed;
  };

  Embroidery3D.prototype._render = function () {
    if (this.renderer && this.mesh) this.renderer.render(this.scene, this.camera);
  };

  /* Smoothly ease displayed progress toward the scroll target. */
  Embroidery3D.prototype._tick = function () {
    var self = this;
    this.raf = 0;
    var diff = this.target - this.cur;
    if (Math.abs(diff) < 0.0008) {
      this.cur = this.target;
      if (this._applyProgress(this.cur)) this._render();
      return;
    }
    this.cur += diff * 0.2;
    this._applyProgress(this.cur);
    this._render();
    this.raf = requestAnimationFrame(function () { self._tick(); });
  };

  Embroidery3D.prototype.setProgress = function (p) {
    this.target = clamp(p, 0, 1);
    if (!this.built) return;                 // applied once the mesh is built
    if (this.reduced) { this.cur = this.target; this._applyProgress(this.cur); this._render(); return; }
    if (!this.raf) { var self = this; this.raf = requestAnimationFrame(function () { self._tick(); }); }
  };

  Embroidery3D.prototype._bindScroll = function () {
    if (this.reduced || !window.ScrollTrigger) { if (!this.reduced) this.setProgress(0); return; }
    var self = this;
    this.st = ScrollTrigger.create({
      trigger: this.el,
      start: 'top 85%',
      end: 'center center',   // fully embroidered by the time it reaches screen centre
      scrub: true,
      onUpdate: function (st) { self.setProgress(st.progress); },
      onRefresh: function (st) { self.setProgress(st.progress); },
      onLeave: function () { self.setProgress(1); },
      onLeaveBack: function () { self.setProgress(0); }
    });
  };

  Embroidery3D.prototype._bindResize = function () {
    var self = this, raf = 0, lastW = this.el.clientWidth;
    var onResize = function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        var w = self.el.clientWidth;
        if (Math.abs(w - lastW) < 2) return;
        lastW = w;
        self._build();
      });
    };
    if (window.ResizeObserver) {
      this._ro = new ResizeObserver(onResize);
      this._ro.observe(this.el);
    } else {
      this._onWinResize = onResize;
      window.addEventListener('resize', onResize);
    }
  };

  Embroidery3D.prototype.destroy = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.st) { try { this.st.kill(); } catch (e) {} this.st = null; }
    if (this._ro) { try { this._ro.disconnect(); } catch (e) {} }
    if (this._onWinResize) window.removeEventListener('resize', this._onWinResize);
    if (this.mesh) { this.scene.remove(this.mesh); this.mesh.dispose && this.mesh.dispose(); }
    if (this.stitchGeo) this.stitchGeo.dispose();
    if (this.material) this.material.dispose();
    if (this.renderer) { try { this.renderer.dispose(); } catch (e) {} }
    this.el.classList.remove('is-active');
    this.el.__e3d = null;
  };

  /* ===================================================================== */
  var controllers = [];
  S.initEmbroidery3D = function (scope) {
    scope = scope || document;

    // prune controllers whose element left the DOM (Barba navigation)
    for (var c = controllers.length - 1; c >= 0; c--) {
      if (!controllers[c].el.isConnected) { controllers[c].destroy(); controllers.splice(c, 1); }
    }

    // Mobile: skip the WebGL embroidery entirely. The plain cursive heading
    // shows instead (legible on small screens) and Three.js never loads.
    if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) return;

    var els = scope.querySelectorAll('[data-embroidery3d]');
    if (!els.length) return;

    Array.prototype.forEach.call(els, function (el) {
      if (el.__e3d || el.__e3dPending) return;      // guard re-entry (event + backstop race)
      if (!webglSupported()) { el.classList.add('e3d--fallback'); return; }
      el.__e3dPending = true;
      var src = el.getAttribute('data-three-src');
      loadThree(src).then(function () {
        el.__e3dPending = false;
        if (el.__e3d || !el.isConnected) return;
        try {
          var ctrl = new Embroidery3D(el);
          el.__e3d = ctrl;
          controllers.push(ctrl);
        } catch (e) {
          el.classList.add('e3d--fallback');
          if (window.console) console.warn('[embroidery3d] init failed', e);
        }
      }).catch(function () { el.__e3dPending = false; el.classList.add('e3d--fallback'); });
    });
  };

  document.addEventListener('suistory:page:init', function (e) {
    S.initEmbroidery3D(e.detail && e.detail.scope);
  });
  // Backstop in case the page:init event fired before this script attached.
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    S.initEmbroidery3D(document);
  } else {
    window.addEventListener('load', function () { S.initEmbroidery3D(document); });
  }
})();
